import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Col, ConfigProvider, Divider, Row } from 'antd';
import { Routes } from '@/routes';
import { Flex, LoadingSpin, PageContainer, PageError } from '@/components';
import { useDevice, useDormitoryGroupManagement, useDormitoryRequests, useValidEntranceDormitory } from '@/hooks';
import ErrorNoValidPreRegister from '../ErrorNoValidPreRegister';
import DormitoryRegisterSolitary from './DormitoryRegisterSolitary';
import DormitoryRegisterGroupCreate from './DormitoryRegisterGroupCreate';
import DormitoryRegisterGroupManagement from './DormitoryRegisterGroupManagement';

const DormitoryRegister = () => {

  const navigate = useNavigate();
  const { isDesktop } = useDevice();
  const { group, fetchGroup, fetching } = useDormitoryGroupManagement();
  const { findRequestBySemesters, error: requestsError, fetchDormitoryRequests, loading: loadingRequests } = useDormitoryRequests(true);
  const { validEntrance, fetchValidEntrance, error: errorEntrance, loading: loadingEntrance } = useValidEntranceDormitory(true);

  const [t] = useTranslation(['common', 'dormitory']);
  const pageTitle = t('dormitory:labels.register');
  const backText = t('common:labels.back');

  const fetchAll = useCallback(() => {
    fetchGroup();
    fetchDormitoryRequests();
    fetchValidEntrance();
  }, []);

  useEffect(fetchAll, []);

  if (loadingRequests || loadingEntrance || fetching) return <Flex align="center" className="h-full pb-20"><LoadingSpin /></Flex>;

  if (errorEntrance) return <PageError title={pageTitle} content={errorEntrance} actionText={backText} action={() => navigate(Routes.Dormitory)} />;
  if (requestsError) return <PageError title={pageTitle} content={requestsError} action={fetchDormitoryRequests} />;

  if (!validEntrance?.inRegistrationPeriod) {
    return <PageError title={pageTitle} content={t('dormitory:errors.not-in-registeration-period')} actionText={backText} action={() => navigate(Routes.Dormitory)} />;
  }

  if (!validEntrance?.registrationEnabled) {
    return <PageError title={pageTitle} content={t('dormitory:errors.register-not-active-for-you')} actionText={backText} action={() => navigate(Routes.Dormitory)} />;
  }

  const validRequest = findRequestBySemesters(validEntrance?.semesters, true);

  if (validRequest == null && validEntrance?.havePreRegToCreateGroup) return <ErrorNoValidPreRegister />;

  return (
    <PageContainer title={pageTitle}>
      <ConfigProvider componentSize={isDesktop() ? 'large' : 'middle'}>
        <Flex direction="column" align="center" className="w-full h-full pb-20">
          <Row justify="center" className="w-full">
            <Col xxl={24} xl={10} lg={12} md={14} xs={24} span={24}>
              <Flex className="text-lg font-bold justify-center">
                <span>{t('dormitory:labels.dormitory')}</span>
                <span>{validRequest?.destinationDormitory?.name}</span>
              </Flex>
              <Divider />
            </Col>
          </Row>
          {validEntrance?.solitaryRegistration ?
            <DormitoryRegisterSolitary onRefresh={fetchAll} validEntrance={validEntrance} group={group} /> :
            (
              <Flex align="center" direction="column" className="mt-2 w-full">
                {group == null || !group.roomSize ?
                  <DormitoryRegisterGroupCreate request={validRequest} onRefresh={fetchAll} /> :
                  <DormitoryRegisterGroupManagement
                    group={group}
                    onRefresh={fetchAll}
                    validEntrance={validEntrance}
                  />
                }
              </Flex>
            )
          }
        </Flex>
      </ConfigProvider>
    </PageContainer>
  );
};

export default DormitoryRegister;
